import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Tag } from "lucide-react";
import outdoorLivingImg from "@/assets/packages/mp-paver-patio-outdoor-living-package-paramus-nj.webp";
import backyardRetreatImg from "@/assets/packages/mp-backyard-retreat-package-montclair-nj.webp";
import drivewayUpgradeImg from "@/assets/packages/mp-paver-driveway-upgrade-oakland-nj.webp";
import curbAppealImg from "@/assets/packages/mp-paver-walkway-curb-appeal-package-wayne-nj.webp";
import outdoorKitchenImg from "@/assets/packages/outdoor-kitchen-installation-wayne-nj.webp";
import patioRestorationImg from "@/assets/packages/mp-paver-patio-restoration-package-bloomfield-nj.webp";
import retainingWallDrainageImg from "@/assets/packages/mp-retaining-wall-drainage-package-paramus-nj.webp";

interface Package {
  id: string;
  number: string;
  title: string;
  tags: string[];
  description: string;
  startingPrice: string;
  serviceLink: string;
  serviceLinkLabel: string;
  alt: string;
  image?: string;
}

const packages: Package[] = [
  {
    id: "outdoor-living",
    number: "01",
    title: "Outdoor Living Package",
    tags: ["Paver Patio", "Seating Wall", "Fire Pit"],
    description:
      "Create a backyard you can actually use, with a paver patio, a built-in fire pit, and a low seating wall that ties it together. We install these across Passaic, Bergen, Essex, and Morris County homes, and the base prep underneath is what keeps the whole thing from shifting after a few winters.",
    startingPrice: "Starting from $12,950",
    serviceLink: "/paving-services/paver-patios",
    serviceLinkLabel: "See paver patios",
    alt: "Paver patio with fire pit and low retaining wall in the outdoor living package at Masonry Pro Construction in Paramus, NJ.",
    image: outdoorLivingImg,
  },
  {
    id: "outdoor-kitchen",
    number: "02",
    title: "Outdoor Kitchen Package",
    tags: ["Built-in Grill", "Stone Counter", "Patio Base"],
    description:
      "Built for homeowners who want to cook and host outside, this one centers on a stone-faced outdoor kitchen island with a built-in grill, wrapped by a paver patio sized for a dining area. We install these across Bergen, Passaic, Essex, and Morris County for people who basically want a second kitchen out back, and we handle the gas, drainage, and counter layout so it works the way you'd actually use it.",
    startingPrice: "Starting from $15,950",
    serviceLink: "/masonry-services/outdoor-kitchens",
    serviceLinkLabel: "See outdoor kitchens",
    alt: "Outdoor kitchen package from Masonry Pro Construction with stone island and grill in Wayne, Passaic County, NJ.",
    image: outdoorKitchenImg,
  },
  {
    id: "backyard-retreat",
    number: "03",
    title: "Backyard Retreat Package",
    tags: ["Outdoor Kitchen", "Fireplace", "Paver Patio"],
    description:
      "For homeowners who entertain, this one pairs a larger paver patio with an outdoor kitchen and a built-in fire pit. It's a popular setup on bigger Morris County lots where there's room to spread out, and we coordinate the gas, drainage, and layout so it works as one space.",
    startingPrice: "Starting from $19,950",
    serviceLink: "/masonry-services/outdoor-kitchens",
    serviceLinkLabel: "Talk to us about a custom build",
    alt: "Backyard retreat with paver patio, fire pit, and outdoor kitchen in Montclair, Essex County, NJ.",
    image: backyardRetreatImg,
  },
  {
    id: "curb-appeal",
    number: "04",
    title: "Curb Appeal Package",
    tags: ["Front Steps", "Stoop", "Walkway"],
    description:
      "A front-of-house refresh that bundles a new walkway, masonry steps or a stoop, and stone veneer on the entry or foundation. Older Colonials and Capes around Essex County tend to have settling front steps, so we rebuild the base instead of just resurfacing.",
    startingPrice: "Starting from $9,950",
    serviceLink: "/masonry-services/steps-stoops",
    serviceLinkLabel: "See steps & stoops",
    alt: "Paver walkway and stone steps at a home with the curb appeal package by Masonry Pro Construction, Wayne, Passaic County, NJ.",
    image: curbAppealImg,
  },
  {
    id: "driveway-upgrade",
    number: "05",
    title: "Driveway Upgrade Package",
    tags: ["Paver Driveway", "Apron", "Border"],
    description:
      "Replace a cracked or worn driveway with pavers, and we can add a Belgian block border for a cleaner edge. Freeze-thaw cycles are rough on driveways here, so we focus on the base and drainage that keep pavers from heaving.",
    startingPrice: "Starting from $8,000",
    serviceLink: "/paving-services/paver-driveways",
    serviceLinkLabel: "See paver driveways",
    alt: "New paver driveway upgrade by Masonry Pro Construction at a home in Oakland, Bergen County, NJ.",
    image: drivewayUpgradeImg,
  },
  {
    id: "drainage-retaining-wall",
    number: "06",
    title: "Retaining Wall & Drainage Package",
    tags: ["Engineered Wall", "Drainage", "Grading"],
    description:
      "If your yard slopes or pools water after storms, this combines a structural retaining wall with regrading to move water away from the house. North Jersey's clay-heavy soil holds moisture, which is part of why these are some of our most requested jobs.",
    startingPrice: "Starting from $5,000",
    serviceLink: "/masonry-services/retaining-walls",
    serviceLinkLabel: "See retaining walls",
    alt: "Retaining wall and drainage package from Masonry Pro Construction in Paramus, Bergen County, NJ.",
    image: retainingWallDrainageImg,
  },
  {
    id: "patio-restoration",
    number: "07",
    title: "Patio Restoration Package",
    tags: ["Re-Level", "Re-Sand", "Re-Seal"],
    description:
      "For patios that have started sinking, separating, or growing weeds in the joints, we re-level the surface, reset the base where needed, and lock it back up with fresh polymeric sand. A lot of Wayne and Clifton homeowners call about this after a contractor skipped the base work the first time.",
    startingPrice: "Starting from $3,000",
    serviceLink: "/paving-services/paver-patio-repair",
    serviceLinkLabel: "See paver patio repair",
    alt: "Paver patio restoration and landscaping in a residential backyard in Bloomfield, Essex County, NJ.",
    image: patioRestorationImg,
  },
];

const Packages = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToHash = () => {
      const hash = window.location.hash?.replace("#", "");
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) {
        // small delay to ensure layout has settled
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    };

    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, [location.hash]);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://masonry-pro.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://masonry-pro.com/services" },
      { "@type": "ListItem", position: 3, name: "Project Packages", item: "https://masonry-pro.com/packages" },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Project Packages Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Pre-priced outdoor living, kitchen, driveway, and patio packages from Masonry Pro Construction in Totowa, NJ. Financing available across North Jersey." />
        <meta property="og:description" content="Pre-priced outdoor living, kitchen, driveway, and patio packages from Masonry Pro Construction in Totowa, NJ. Financing available across North Jersey." />
        <meta property="og:title" content="Project Packages Totowa, NJ | Masonry Pro Construction" />
        <meta
          property="og:description"
          content="Pre-priced outdoor living, kitchen, driveway, retaining wall, and patio restoration packages from a licensed North Jersey masonry contractor."
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <Header />
      <Breadcrumb />

      <main>
        {/* Hero */}
        <section
          className="relative overflow-hidden"
          style={{ backgroundColor: "#0d1017", paddingTop: "160px", paddingBottom: "80px" }}
        >
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1
                style={{
                  color: "#f5f5f5",
                  fontSize: "3rem",
                  fontWeight: "bold",
                  lineHeight: 1.1,
                  marginBottom: "1.5rem",
                }}
              >
                Project Packages from Masonry Pro in <span className="text-gradient-gold">Totowa, NJ</span> Serving North Jersey
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Seven of our most-requested builds, bundled with the materials, prep, and detailing we actually use on every job. Starting prices below are real-world ranges for North Jersey, and final scope is set after a site visit.
              </p>
              <Link
                to="/financing"
                className="inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/10 px-4 py-2 text-sm text-secondary hover:bg-secondary/20 transition-colors"
              >
                <Tag className="h-4 w-4" />
                Financing available through Acorn Finance
              </Link>
            </div>
          </div>
        </section>

        {/* Package sections */}
        <div className="bg-background">
          {packages.map((pkg, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <section
                key={pkg.id}
                id={pkg.id}
                className="relative scroll-mt-28 py-16 md:py-24 border-b border-border/40 overflow-hidden"
              >
                {/* Faded oversized numeral — desktop only */}
                <span
                  aria-hidden="true"
                  className="hidden md:block pointer-events-none absolute -top-6 right-4 lg:right-12 -z-0 select-none font-black leading-none text-secondary/10 text-[10rem] lg:text-[14rem]"
                >
                  {pkg.number}
                </span>

                <div className="container mx-auto px-4 relative z-10">
                  <div
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                      reverse ? "lg:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    {/* Image */}
                    <div className="w-full">
                      <div className="rounded-lg overflow-hidden border border-border bg-card shadow-lg">
                        <AspectRatio ratio={4 / 3}>
                          <img
                            src={pkg.image ?? "/placeholder.svg"}
                            alt={pkg.alt}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        </AspectRatio>
                      </div>
                    </div>

                    {/* Content */}
                    <div>
                      <div className="text-sm font-semibold tracking-widest text-secondary mb-3">
                        PACKAGE {pkg.number}
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-4">{pkg.title}</h2>

                      <div className="flex flex-wrap gap-2 mb-5">
                        {pkg.tags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="outline"
                            className="border-secondary/40 text-secondary bg-secondary/5"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {pkg.description}
                      </p>

                      {/* Price tag */}
                      <div className="inline-flex items-center gap-2 rounded-md bg-secondary text-secondary-foreground font-bold px-4 py-2 mb-6 shadow-sm">
                        <Tag className="h-4 w-4" />
                        {pkg.startingPrice}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <Button asChild variant="gold">
                          <Link to="/contact">Get a free estimate</Link>
                        </Button>
                        <Link
                          to={pkg.serviceLink}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline self-center"
                        >
                          {pkg.serviceLinkLabel}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Closing CTA */}
        <section className="py-20 bg-card">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Not sure which package fits your yard?
            </h2>
            <p className="text-muted-foreground mb-8">
              Send us a quick description of your project and we'll tell you honestly which package is closest, or whether you need something custom.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild variant="gold" size="lg">
                <Link to="/contact">Get a free estimate</Link>
              </Button>
              <Button asChild variant="goldOutline" size="lg">
                <Link to="/financing">See financing options</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              Financing through Acorn Finance. Subject to credit approval.
            </p>
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
      <AccessibilityWidget />
    </div>
  );
};

export default Packages;